/**
 * Event Auto-Tag Utility
 *
 * When a trade is entered within a short window of an economic/news event,
 * automatically tag that trade with the event name. Also used by the manual
 * "Tag all today" buttons on the News & Economic Events panel.
 *
 * Behavior:
 *   - Pure: accepts trades[] + events[] and returns new trades[] (never mutates)
 *   - Idempotent: never adds a tag that's already there
 *   - Safe: leaves tags untouched for trades missing entryTime
 *
 * Conventions for matching:
 *   - `event.time` is a "HH:MM" string (ET)
 *   - `trade.entryTime` can be "HH:MM", "HH:MM:SS", or "YYYY-MM-DD HH:MM AM/PM"
 *   - Proximity window defaults to 30 minutes (consumer can override)
 */

import { parseHM } from '../services/timeService';

export const DEFAULT_PROXIMITY_MIN = 30;

// timeToMinutes is sourced from the central timeService. Re-exported as an
// alias so external callers (and tests) that imported `timeToMinutes` from
// this module keep working.
export const timeToMinutes = parseHM;

/**
 * Is `trade` within `windowMin` minutes of `event.time`?
 */
export const isTradeNearEvent = (trade, event, windowMin = DEFAULT_PROXIMITY_MIN) => {
  if (!trade || !event || !event.time) return false;
  const t = timeToMinutes(trade.entryTime);
  const e = timeToMinutes(event.time);
  if (t === null || e === null) return false;
  return Math.abs(t - e) <= windowMin;
};

/**
 * Return a new trade object with `eventName` added to its tags if (a) not
 * already present and (b) eventName is a non-empty string. Otherwise returns
 * the trade unchanged.
 */
export const addTagToTrade = (trade, eventName) => {
  if (!trade || !eventName) return trade;
  const name = String(eventName).trim();
  if (!name) return trade;
  const existing = Array.isArray(trade.tags) ? trade.tags : [];
  if (existing.includes(name)) return trade;
  return { ...trade, tags: [...existing, name] };
};

/**
 * Given a list of day trades and one event, return a new trades[] where
 * every trade within the proximity window has the event name added to
 * its tags. Trades outside the window are returned unchanged.
 *
 * If `includeAll=true`, ignores the proximity check and tags every trade.
 */
export const tagTradesWithEvent = (trades, event, { windowMin = DEFAULT_PROXIMITY_MIN, includeAll = false } = {}) => {
  if (!Array.isArray(trades) || trades.length === 0) return trades || [];
  if (!event || !event.name) return trades;
  return trades.map(t => {
    if (includeAll || isTradeNearEvent(t, event, windowMin)) {
      return addTagToTrade(t, event.name);
    }
    return t;
  });
};

/**
 * Given a list of day trades and a list of events, return a new trades[]
 * where each trade is tagged with every nearby event. Uses the proximity
 * window independently per event.
 */
export const autoTagTradesFromEvents = (trades, events, { windowMin = DEFAULT_PROXIMITY_MIN } = {}) => {
  if (!Array.isArray(trades) || trades.length === 0) return trades || [];
  if (!Array.isArray(events) || events.length === 0) return trades;
  let out = trades;
  for (const ev of events) {
    if (!ev || !ev.name) continue;
    out = tagTradesWithEvent(out, ev, { windowMin });
  }
  return out;
};

/**
 * Compute the diff between two trade lists for logging / toast purposes.
 * Returns { changedCount, addedTagPairs } where addedTagPairs is a list of
 * [tradeId, eventName] pairs that were newly applied.
 */
export const diffTagApplications = (before, after) => {
  const byId = new Map();
  (before || []).forEach(t => byId.set(t.id, t));
  const pairs = [];
  let changed = 0;
  (after || []).forEach(t => {
    const prev = byId.get(t.id);
    if (!prev) return;
    const oldTags = new Set(Array.isArray(prev.tags) ? prev.tags : []);
    const newTags = Array.isArray(t.tags) ? t.tags : [];
    let tradeChanged = false;
    for (const tag of newTags) {
      if (!oldTags.has(tag)) {
        pairs.push([t.id, tag]);
        tradeChanged = true;
      }
    }
    if (tradeChanged) changed++;
  });
  return { changedCount: changed, addedTagPairs: pairs };
};

export default {
  DEFAULT_PROXIMITY_MIN,
  timeToMinutes,
  isTradeNearEvent,
  addTagToTrade,
  tagTradesWithEvent,
  autoTagTradesFromEvents,
  diffTagApplications
};
